import { validateCmdbListSearch } from "@/components/cmdb/ciFilterModel";
import { validateCiDetailSearch } from "@/components/cmdb/ciDetailModel";
import { CiDetailPage as CmdbCiDetailPage } from "@/routes/agent/cmdb/CiDetailPage";
import { CiEditRoute, CiNewRoute } from "@/routes/agent/cmdb/CiEditPage";
import { CmdbImportPage } from "@/routes/agent/cmdb/CmdbImportPage";
import { CmdbListPage } from "@/routes/agent/cmdb/CmdbListPage";
import { CmdbCatalogPage } from "@/routes/admin/cmdb/CmdbCatalogPage";
import { CmdbClassEditorPage } from "@/routes/admin/cmdb/CmdbClassEditorPage";
import { CmdbClassesPage } from "@/routes/admin/cmdb/CmdbClassesPage";
import { PluginRoutePage } from "@/routes/agent/PluginRoutePage";
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  redirect,
} from "@tanstack/react-router";
import { SetPasswordPage } from "@/routes/SetPasswordPage";
import { LoginPage } from "@/routes/LoginPage";
import { DashboardPage, type DashboardSearch } from "@/routes/agent/DashboardPage";
import { WallboardPage } from "@/routes/agent/WallboardPage";
import { KioskWallboardPage } from "@/routes/KioskWallboardPage";
import { ReportsPage, type ReportsSearch } from "@/routes/agent/ReportsPage";
import { ReportBuilderPage } from "@/routes/agent/ReportBuilderPage";
import { validateReportBuilderSearch } from "@/routes/agent/reportBuilderSearch";
import { ReportViewPage } from "@/routes/agent/ReportViewPage";
import { ReportSchedulesPage } from "@/routes/agent/ReportSchedulesPage";
import { ReportScheduleEditorPage } from "@/routes/agent/ReportScheduleEditorPage";
import { validateScheduleNewSearch } from "@/routes/agent/reportScheduleSearch";
import { validateReportViewSearch } from "@/routes/agent/reportViewSearch";
import { SavedSearchPage } from "@/routes/agent/SavedSearchPage";
import { SavedSearchEditorPage } from "@/routes/agent/SavedSearchEditorPage";
import { DrillPage } from "@/dashboard/drill/DrillPage";
import { validateDrillSearch } from "@/dashboard/drill/drillLinks";
import { validateWallboardSearch } from "@/dashboard/wallboard/search";
import { validateSavedSearchSearch } from "@/routes/agent/savedSearchSearch";
import { QueuesPage, type QueuesSearch } from "@/routes/agent/QueuesPage";
import { TicketZoomPage } from "@/routes/agent/TicketZoomPage";
import { SearchPage, type SearchSearch } from "@/routes/agent/SearchPage";
import { KbPage, type KbSearch } from "@/routes/agent/KbPage";
import { KbArticlePage as AgentKbArticlePage } from "@/routes/agent/KbArticlePage";
import { SecurityPage } from "@/routes/agent/SecurityPage";
import { CalendarPage } from "@/routes/agent/CalendarPage";
import { TemplatesPage as AgentTemplatesPage } from "@/routes/agent/TemplatesPage";
import {
  NewTicketPage as AgentNewTicketPage,
  type NewTicketSearch,
} from "@/routes/agent/NewTicketPage";
import { NewProcessTicketPage, type NewProcessTicketSearch } from "@/routes/agent/NewProcessTicketPage";
import {
  KbArticleNewPage,
  KbArticleEditPage,
} from "@/routes/agent/KbArticleEditorPage";
import {
  ServiceViewPage,
  type ServiceViewSearch,
} from "@/routes/agent/ServiceViewPage";
import {
  TimeAccountingReportPage,
  type TimeAccountingSearch,
} from "@/routes/agent/TimeAccountingReportPage";
import { CustomerDetailPage } from "@/routes/agent/CustomerDetailPage";
import {
  CustomerDirectoryPage,
  type CustomerDirectorySearch,
} from "@/routes/agent/CustomerDirectoryPage";
import { DialPage } from "@/routes/agent/DialPage";
import { validateDialSearch } from "@/routes/agent/dialSearch";
import type { TicketListChannel } from "@/lib/api";
import { TICKET_CHANNEL_KEYS } from "@/lib/ticketChannel";
import { TicketAttributeRelationsPage } from "@/routes/admin/TicketAttributeRelationsPage";
import { AgentShell } from "@/components/layout/AgentShell";
import { PortalShell } from "@/components/layout/PortalShell";
import { RequireAuth } from "@/auth/RequireAuth";
import { RequirePortalAuth } from "@/auth/RequirePortalAuth";
import { CustomerAuthProvider } from "@/auth/CustomerAuthContext";
import { RequirePortalEnabled } from "@/components/portal/RequirePortalEnabled";
import { HomeRedirect } from "@/routes/HomeRedirect";
import { PortalLoginPage } from "@/routes/portal/PortalLoginPage";
import {
  TicketListPage,
  type PortalTicketListSearch,
} from "@/routes/portal/TicketListPage";
import { NewTicketPage, type PortalNewTicketSearch } from "@/routes/portal/NewTicketPage";
import { PortalCisPage } from "@/routes/portal/PortalCisPage";
import { ChangeSchedulePage as PortalChangeSchedulePage } from "@/routes/portal/ChangeSchedulePage";
import { PortalCiPage } from "@/routes/portal/PortalCiPage";
import {
  NewProcessTicketPage as PortalNewProcessTicketPage,
  type PortalNewProcessTicketSearch,
} from "@/routes/portal/NewProcessTicketPage";
import { TicketDetailPage } from "@/routes/portal/TicketDetailPage";
import { KbSearchPage, type PortalKbSearch } from "@/routes/portal/KbSearchPage";
import { KbArticlePage } from "@/routes/portal/KbArticlePage";
import { PreferencesPage as PortalPreferencesPage } from "@/routes/portal/PreferencesPage";
import { SecurityPage as PortalSecurityPage } from "@/routes/portal/SecurityPage";
import { AdminShell } from "@/components/layout/AdminShell";
import { RequireAdmin } from "@/auth/RequireAdmin";
import { AdminHomePage } from "@/routes/admin/AdminHomePage";
import { UsersPage } from "@/routes/admin/UsersPage";
import { GroupsPage } from "@/routes/admin/GroupsPage";
import { RolesPage } from "@/routes/admin/RolesPage";
import { AgentRolesPage } from "@/routes/admin/AgentRolesPage";
import { AgentGroupsPage } from "@/routes/admin/AgentGroupsPage";
import { RoleGroupsPage } from "@/routes/admin/RoleGroupsPage";
import { CustomerUserCustomersPage } from "@/routes/admin/CustomerUserCustomersPage";
import { CustomerUserGroupsPage } from "@/routes/admin/CustomerUserGroupsPage";
import { QueuesPage as AdminQueuesPage } from "@/routes/admin/QueuesPage";
import { StatesPage } from "@/routes/admin/StatesPage";
import { PrioritiesPage } from "@/routes/admin/PrioritiesPage";
import { TypesPage } from "@/routes/admin/TypesPage";
import { ServicesPage } from "@/routes/admin/ServicesPage";
import { SlasPage } from "@/routes/admin/SlasPage";
import { SystemAddressesPage } from "@/routes/admin/SystemAddressesPage";
import { NotificationEventsPage } from "@/routes/admin/NotificationEventsPage";
import { CustomerUsersPage } from "@/routes/admin/CustomerUsersPage";
import { CustomerCompaniesPage } from "@/routes/admin/CustomerCompaniesPage";
import { TemplatesPage } from "@/routes/admin/TemplatesPage";
import { TemplateAttachmentsPage } from "@/routes/admin/TemplateAttachmentsPage";
import { AttachmentsPage } from "@/routes/admin/AttachmentsPage";
import { QueueTemplatesPage } from "@/routes/admin/QueueTemplatesPage";
import { QueueAutoResponsesPage } from "@/routes/admin/QueueAutoResponsesPage";
import { SalutationsPage } from "@/routes/admin/SalutationsPage";
import { SignaturesPage } from "@/routes/admin/SignaturesPage";
import { AutoResponsesPage } from "@/routes/admin/AutoResponsesPage";
import { DynamicFieldsPage } from "@/routes/admin/DynamicFieldsPage";
import { WebhooksPage } from "@/routes/admin/WebhooksPage";
import { WebservicesPage } from "@/routes/admin/WebservicesPage";
import { ItsmPage } from "@/routes/admin/ItsmPage";
import { ChangeAdminPage } from "@/routes/admin/itsm/ChangeAdminPage";
import { parseChangeAdminTab, type ChangeAdminTab } from "@/routes/admin/itsm/changeAdminTabs";
import { ChangeTemplateEditorPage } from "@/routes/admin/itsm/ChangeTemplateEditorPage";
import { ApprovalsOverviewPage } from "@/routes/admin/approvals/ApprovalsOverviewPage";
import { ApprovalTemplatesPage } from "@/routes/admin/approvals/ApprovalTemplatesPage";
import { ApprovalListsPage } from "@/routes/admin/approvals/ApprovalListsPage";
import { ApprovalDelegationsPage } from "@/routes/admin/approvals/ApprovalDelegationsPage";
import { ApprovalsPage } from "@/routes/agent/ApprovalsPage";
import { parseApprovalsSearch } from "@/routes/agent/approvalsSearch";
import { ApprovalLinkPage } from "@/routes/ApprovalLinkPage";
import { PortalApprovalsPage } from "@/routes/portal/ApprovalsPage";
import { PortalApprovalPage } from "@/routes/portal/ApprovalPage";
import { LinkTypesPage } from "@/routes/admin/LinkTypesPage";
import { ItsmImportPage } from "@/routes/admin/ItsmImportPage";
import { CiListPage } from "@/routes/agent/CiListPage";
import { ChangeListPage } from "@/routes/agent/itsm/ChangeListPage";
import { ChangeDetailPage } from "@/routes/agent/itsm/ChangeDetailPage";
import { ChangeCalendarPage } from "@/routes/agent/itsm/ChangeCalendarPage";
import { CabMeetingsPage } from "@/routes/agent/itsm/CabMeetingsPage";
import { CabMeetingPage } from "@/routes/agent/itsm/CabMeetingPage";
import { LegacyChangeListPage } from "@/routes/agent/itsm/LegacyChangeListPage";
import { parseLegacyListSearch } from "@/components/itsm/change/changeUi";
import { LegacyChangeDetailPage } from "@/routes/agent/itsm/LegacyChangeDetailPage";
import { CiDetailPage } from "@/routes/agent/CiDetailPage";
import { WebserviceDetailPage } from "@/routes/admin/WebserviceDetailPage";
import { WEBSERVICE_TABS, type WebserviceTab } from "@/lib/webservicesApi";
import { MailOutboundPage } from "@/routes/admin/MailOutboundPage";
import { PgpKeysPage } from "@/routes/admin/PgpKeysPage";
import { SmimePage } from "@/routes/admin/SmimePage";
import { MailAccountsPage } from "@/routes/admin/MailAccountsPage";
import { TelegramChannelPage } from "@/routes/admin/TelegramChannelPage";
import { WebchatConfigsPage } from "@/routes/admin/WebchatConfigsPage";
import { WebchatConfigEditorPage } from "@/routes/admin/WebchatConfigEditorPage";
import { OAuth2TokensPage } from "@/routes/admin/OAuth2TokensPage";
import { MailLogPage } from "@/routes/admin/MailLogPage";
import { SubjectConfigPage } from "@/routes/admin/SubjectConfigPage";
import { SettingsSectionPage } from "@/routes/admin/SettingsSectionPage";
import { DaemonsPage } from "@/routes/admin/DaemonsPage";
import { PluginsPage } from "@/routes/admin/PluginsPage";
import { DashboardKioskLinksPage } from "@/routes/admin/DashboardKioskLinksPage";
import { DashboardTemplatesPage } from "@/routes/admin/DashboardTemplatesPage";
import { ReportingAdminPage } from "@/routes/admin/ReportingAdminPage";
import { SystemInfoPage } from "@/routes/admin/SystemInfoPage";
import { PostmasterFiltersPage } from "@/routes/admin/PostmasterFiltersPage";
import { PostmasterFilterDetailPage } from "@/routes/admin/PostmasterFilterDetailPage";
import { AclPage } from "@/routes/admin/AclPage";
import { AclDetailPage } from "@/routes/admin/AclDetailPage";
import { GenericAgentJobsPage } from "@/routes/admin/GenericAgentJobsPage";
import { GenericAgentJobDetailPage } from "@/routes/admin/GenericAgentJobDetailPage";
import { ProcessesPage } from "@/routes/admin/ProcessesPage";
import { QueueVariablesPage } from "@/routes/admin/QueueVariablesPage";
import { QueueCustomerLinksPage } from "@/routes/admin/QueueCustomerLinksPage";
import { CustomerFieldsPage } from "@/routes/admin/CustomerFieldsPage";
import { ApiKeysPage } from "@/routes/admin/ApiKeysPage";
import { AuthConfigPage } from "@/routes/admin/AuthConfigPage";
import { IdentityProvidersPage } from "@/routes/admin/IdentityProvidersPage";
import {
  IdentityProviderPage,
  type IdentityProviderSearch,
} from "@/routes/admin/IdentityProviderPage";
import { GdprPage, type GdprSearch } from "@/routes/admin/GdprPage";
import { AiSettingsPage } from "@/routes/admin/AiSettingsPage";
import { AiProvidersPage } from "@/routes/admin/AiProvidersPage";
import { AiModelsPage } from "@/routes/admin/AiModelsPage";
import { AiMcpClientsPage } from "@/routes/admin/AiMcpClientsPage";
import { AiQueuePoliciesPage } from "@/routes/admin/AiQueuePoliciesPage";
import {
  AiQueuePolicyNewPage,
  AiQueuePolicyEditPage,
} from "@/routes/admin/AiQueuePolicyEditorPage";
import { AiAuditPage } from "@/routes/admin/AiAuditPage";
import { AiAclPage } from "@/routes/admin/AiAclPage";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: HomeRedirect,
});

function searchFlag(v: unknown): string | undefined {
  return typeof v === "string" || typeof v === "number" || typeof v === "boolean"
    ? String(v)
    : undefined;
}

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    next?: string;
    sso_error?: string;
    logged_out?: string;
    sso_step_up?: "1";
  } => ({
    next: typeof s.next === "string" ? s.next : undefined,
    // The backend's "1" sends the browser back to finish a second factor after SSO.
    sso_step_up: searchFlag(s.sso_step_up) === "1" ? "1" : undefined,
    // The router JSON-parses search values: `?sso_error=1` arrives as a number.
    sso_error: searchFlag(s.sso_error),
    // Only the backend's "1" marks a sign-out; "0"/"false" must not show the note.
    logged_out: searchFlag(s.logged_out) === "1" ? "1" : undefined,
  }),
  component: LoginPage,
});

// Public wallboard: the link's secret is the URL fragment, no auth gate and not inside the agent
// layout (like /login). Everything it loads comes from /api/v1/kiosk.
const kioskWallboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/wallboard",
  component: KioskWallboardPage,
});

// Public: the one-time link a new agent gets by mail. No auth gate — the
// token in the query string is the credential.
const setPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/set-password",
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({
    token: typeof s.token === "string" ? s.token : undefined,
  }),
  component: SetPasswordPage,
});

// Public: the decision link of an approval e-mail. The token travels in the URL fragment
// (never in the query), the page strips it from the address bar.
const approvalLinkRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/approve",
  component: ApprovalLinkPage,
});

const agentLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agent",
  component: () => (
    <RequireAuth>
      <AgentShell>
        <Outlet />
      </AgentShell>
    </RequireAuth>
  ),
});

const agentIndexRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/",
  // `?d=<dashboard id>`; the router JSON-parses search values, so "7" arrives as a number.
  validateSearch: (s: Record<string, unknown>): DashboardSearch => {
    const raw = typeof s.d === "string" && /^\d+$/.test(s.d) ? Number(s.d) : s.d;
    return typeof raw === "number" && Number.isSafeInteger(raw) && raw > 0 ? { d: raw } : {};
  },
  component: DashboardPage,
});

// TV mode: `?ids=7,9` (at most 10) and `?rotate=` seconds (15..900).
const agentWallboardRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/wallboard",
  validateSearch: validateWallboardSearch,
  component: WallboardPage,
});

const agentQueuesRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/queues",
  validateSearch: (s: Record<string, unknown>): QueuesSearch => {
    const num = (v: unknown) =>
      typeof v === "number"
        ? v
        : typeof v === "string" && v !== ""
          ? Number(v)
          : undefined;
    const sort = s.sort as QueuesSearch["sort"];
    const order = s.order === "asc" || s.order === "desc" ? s.order : undefined;
    const state =
      s.state_type === "todo" ||
      s.state_type === "new" ||
      s.state_type === "open" ||
      s.state_type === "pending" ||
      s.state_type === "closed" ||
      s.state_type === "all"
        ? s.state_type
        : undefined;
    // Customer numbers are typically all-digit strings (e.g. "10042"), and
    // the router's default search codec parses those as JS numbers rather
    // than leaving them as strings — accept both and normalize to a string
    // so `customer_id` round-trips through the URL correctly.
    const customerId =
      typeof s.customer_id === "string" && s.customer_id !== ""
        ? s.customer_id
        : typeof s.customer_id === "number"
          ? String(s.customer_id)
          : undefined;
    // One value comes back from the URL as a plain string, several as an array.
    const channels = (v: unknown): QueuesSearch["channel"] => {
      const list = (Array.isArray(v) ? v : [v]).filter(
        (c): c is TicketListChannel =>
          typeof c === "string" && (TICKET_CHANNEL_KEYS as string[]).includes(c),
      );
      return list.length ? [...new Set(list)] : undefined;
    };
    const bool = (v: unknown): true | undefined =>
      v === true || v === "true" || v === 1 || v === "1" ? true : undefined;
    const view =
      s.view === "locked" ||
      s.view === "mine" ||
      s.view === "responsible" ||
      s.view === "watched" ||
      s.view === "escalated" ||
      s.view === "service"
        ? s.view
        : undefined;
    return {
      queue_id: num(s.queue_id),
      state_type: state,
      customer_id: customerId,
      owner_id: num(s.owner_id),
      responsible_id: num(s.responsible_id),
      service_id: num(s.service_id),
      locked: bool(s.locked),
      watcher_user_id: num(s.watcher_user_id),
      escalated: bool(s.escalated),
      unassigned: bool(s.unassigned),
      channel: channels(s.channel),
      view,
      offset: num(s.offset),
      limit: num(s.limit),
      sort,
      order,
      include_archived:
        s.include_archived === true || s.include_archived === "true" ? true : undefined,
    };
  },
  component: QueuesPage,
});

/** Epoch-ms search param (CTI popup call times) — positive number or absent. */
function epochMs(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" && v !== "" ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function positiveInt(v: unknown): number | undefined {
  const n = typeof v === "number" ? v : typeof v === "string" && v !== "" ? Number(v) : NaN;
  return Number.isInteger(n) && n > 0 ? n : undefined;
}

// NB: register the literal "/tickets/new" route before the "$ticketId" param
// route so "new" isn't captured as a ticket id.
const agentNewTicketRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/tickets/new",
  validateSearch: (s: Record<string, unknown>): NewTicketSearch => ({
    queue_id:
      typeof s.queue_id === "number"
        ? s.queue_id
        : typeof s.queue_id === "string" && s.queue_id !== ""
          ? Number(s.queue_id)
          : undefined,
    type: s.type === "phone" || s.type === "email" || s.type === "note" ? s.type : undefined,
    direction: s.direction === "inbound" || s.direction === "outbound" ? s.direction : undefined,
    customer: typeof s.customer === "string" && s.customer !== "" ? s.customer : undefined,
    number:
      typeof s.number === "string" && s.number !== ""
        ? s.number
        : typeof s.number === "number"
          ? String(s.number)
          : undefined,
    call_started: epochMs(s.call_started),
    call_ended: epochMs(s.call_ended),
    owner_id: positiveInt(s.owner_id),
    from_call: s.from_call === true || s.from_call === 1 || s.from_call === "1" || s.from_call === "true" ? true : undefined,
    from_change: positiveInt(s.from_change),
    subject: typeof s.subject === "string" && s.subject !== "" ? s.subject.slice(0, 250) : undefined,
  }),
  component: AgentNewTicketPage,
});

// Literal path; no `$param` sibling can capture "process/new".
const agentNewProcessTicketRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/process/new",
  validateSearch: (s: Record<string, unknown>): NewProcessTicketSearch => ({
    process: typeof s.process === "string" && s.process !== "" ? s.process : undefined,
  }),
  component: NewProcessTicketPage,
});

// Literal path, registered before "$ticketId" so "drill" is never read as a ticket id.
const agentDrillRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/tickets/drill",
  validateSearch: validateDrillSearch,
  component: DrillPage,
});

const agentTicketRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/tickets/$ticketId",
  component: TicketZoomPage,
});

const agentSearchRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/search",
  validateSearch: (s: Record<string, unknown>): SearchSearch => {
    const num = (v: unknown): number | undefined => {
      if (typeof v === "number" && Number.isFinite(v)) return v;
      if (typeof v === "string" && v !== "") {
        const n = Number(v);
        return Number.isFinite(n) ? n : undefined;
      }
      return undefined;
    };
    const numList = (v: unknown): number[] | undefined => {
      if (v === undefined || v === null || v === "") return undefined;
      const raw = Array.isArray(v) ? v : [v];
      const out = raw
        .map((x) => num(x))
        .filter((x): x is number => x !== undefined);
      return out.length ? out : undefined;
    };
    const strList = (v: unknown): string[] | undefined => {
      if (v === undefined || v === null || v === "") return undefined;
      const raw = Array.isArray(v) ? v : [v];
      const out = raw
        .filter((x): x is string => typeof x === "string" && x.length > 0)
        .map((x) => x.trim())
        .filter(Boolean);
      return out.length ? out : undefined;
    };
    const isoDate = (v: unknown): string | undefined =>
      typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
    const sortOrder = (v: unknown): SearchSearch["sort"] =>
      v === "created_desc" || v === "created_asc" || v === "changed_desc"
        ? v
        : undefined;

    return {
      q: typeof s.q === "string" ? s.q : undefined,
      offset: num(s.offset),
      queue_id: numList(s.queue_id),
      state_type: strList(s.state_type),
      owner_id: num(s.owner_id),
      customer_id: typeof s.customer_id === "string" ? s.customer_id : undefined,
      customer_label:
        typeof s.customer_label === "string" ? s.customer_label : undefined,
      created_from: isoDate(s.created_from),
      created_to: isoDate(s.created_to),
      sort: sortOrder(s.sort),
      include_archived:
        s.include_archived === true || s.include_archived === "true" ? true : undefined,
    };
  },
  component: SearchPage,
});

const agentKbRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/kb",
  validateSearch: (s: Record<string, unknown>): KbSearch => {
    const num = (v: unknown) =>
      typeof v === "number"
        ? v
        : typeof v === "string" && v !== ""
          ? Number(v)
          : undefined;
    const state =
      s.state === "all" ||
      s.state === "draft" ||
      s.state === "review" ||
      s.state === "published" ||
      s.state === "archived"
        ? s.state
        : undefined;
    return {
      category_id: num(s.category_id),
      state,
      tag: typeof s.tag === "string" && s.tag !== "" ? s.tag : undefined,
      // "articles" is the default and stays out of the URL.
      tab: s.tab === "categories" ? "categories" : undefined,
      new: s.new === true || s.new === 1 || s.new === "1" ? true : undefined,
    };
  },
  component: KbPage,
});

const agentKbNewRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/kb/new",
  component: KbArticleNewPage,
});

const agentKbCategoriesRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/kb/categories",
  // Categories are now the "Kategorien" tab of the KB page. Keep the old URL
  // (and its ?new deep link) working by redirecting to /agent/kb?tab=categories.
  validateSearch: (s: Record<string, unknown>): { new?: boolean } =>
    s.new === true || s.new === 1 || s.new === "1" ? { new: true } : {},
  beforeLoad: ({ search }) => {
    throw redirect({
      to: "/agent/kb",
      search: { tab: "categories", ...(search.new ? { new: true } : {}) },
    });
  },
});

const agentKbArticleRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/kb/$articleId",
  component: AgentKbArticlePage,
});

const agentKbArticleEditRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/kb/$articleId/edit",
  component: KbArticleEditPage,
});

const agentSecurityRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/security",
  component: SecurityPage,
});

// Personal preferences live in the account menu now; keep old bookmarks working.
const agentSettingsRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/settings",
  beforeLoad: () => {
    throw redirect({ to: "/agent" });
  },
});

// The old statistics page lives on as the reports overview; keep bookmarks working.
const agentStatsRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/stats",
  beforeLoad: () => {
    throw redirect({ to: "/agent/reports" });
  },
});

const agentReportsRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/reports",
  validateSearch: (s: Record<string, unknown>): ReportsSearch => ({
    category: typeof s.category === "string" && /^[a-z_]{1,32}$/.test(s.category) ? s.category : undefined,
  }),
  component: ReportsPage,
});

// Static segment first: "new" is never read as a report key.
const agentReportNewRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/reports/new",
  validateSearch: validateReportBuilderSearch,
  component: ReportBuilderPage,
});

// A built-in key is redirected to /reports/new?from=<key> by the page.
const agentReportEditRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/reports/$reportKey/edit",
  component: ReportBuilderPage,
});

// Static segments before "$reportKey": "schedules" is never read as a report key.
const agentReportSchedulesRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/reports/schedules",
  component: ReportSchedulesPage,
});

const agentReportScheduleNewRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/reports/schedules/new",
  validateSearch: validateScheduleNewSearch,
  component: ReportScheduleEditorPage,
});

const agentReportScheduleRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/reports/schedules/$scheduleId",
  component: ReportScheduleEditorPage,
});

const agentReportViewRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/reports/$reportKey",
  validateSearch: validateReportViewSearch,
  component: ReportViewPage,
});

// Static segment first: "new" is never read as a search id.
const agentSearchNewRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/searches/new",
  component: SavedSearchEditorPage,
});

const agentSearchEditRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/searches/$searchId/edit",
  component: SavedSearchEditorPage,
});

// Consumed by the dashboards later: keep the path.
const agentSearchViewRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/searches/$searchId",
  validateSearch: validateSavedSearchSearch,
  component: SavedSearchPage,
});

// Static segment before the param route: "cis" is never read as a CI source.
const agentItsmCisRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/cis",
  component: CiListPage,
});

// Tiqora CIs (Mode B) live in the CMDB: /agent/itsm/cis/tiqora/<id> is only a stable alias.
const agentItsmCiDetailRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/cis/$source/$ciId",
  beforeLoad: ({ params }) => {
    if (params.source === "tiqora") {
      throw redirect({ to: "/agent/cmdb/$ciId", params: { ciId: params.ciId }, replace: true });
    }
  },
  component: CiDetailPage,
});

// Change management (I19). Static segments before the param routes: "calendar" is never read as a ticket id.
const agentChangesRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/changes",
  component: ChangeListPage,
});

const agentChangeCalendarRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/changes/calendar",
  component: ChangeCalendarPage,
});

const agentChangeRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/changes/$ticketId",
  component: ChangeDetailPage,
});

const agentCabMeetingsRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/cab-meetings",
  component: CabMeetingsPage,
});

const agentCabMeetingRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/cab-meetings/$meetingId",
  component: CabMeetingPage,
});

const agentLegacyChangesRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/legacy-changes",
  // `?ticket_id=` comes from the ticket zoom in Mode A (changes linked to that ticket).
  validateSearch: parseLegacyListSearch,
  component: LegacyChangeListPage,
});

const agentLegacyChangeRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/itsm/legacy-changes/$changeId",
  component: LegacyChangeDetailPage,
});

const agentCalendarRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/calendar",
  component: CalendarPage,
});

const agentTemplatesRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/templates",
  component: AgentTemplatesPage,
});

const agentServicesRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/services",
  validateSearch: (s: Record<string, unknown>): ServiceViewSearch => {
    const num = (v: unknown) =>
      typeof v === "number"
        ? v
        : typeof v === "string" && v !== ""
          ? Number(v)
          : undefined;
    const state =
      s.state_type === "new" ||
      s.state_type === "open" ||
      s.state_type === "pending" ||
      s.state_type === "closed" ||
      s.state_type === "all"
        ? s.state_type
        : undefined;
    return {
      service_id: num(s.service_id),
      state_type: state,
      offset: num(s.offset),
    };
  },
  component: ServiceViewPage,
});

const agentTimeAccountingRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/time-accounting",
  validateSearch: (s: Record<string, unknown>): TimeAccountingSearch => {
    const num = (v: unknown) =>
      typeof v === "number"
        ? v
        : typeof v === "string" && v !== ""
          ? Number(v)
          : undefined;
    const isoDate = (v: unknown): string | undefined =>
      typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v) ? v : undefined;
    return {
      create_by: num(s.create_by),
      ticket_id: num(s.ticket_id),
      created_from: isoDate(s.created_from),
      created_to: isoDate(s.created_to),
      offset: num(s.offset),
    };
  },
  component: TimeAccountingReportPage,
});

const agentCustomersRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/customers",
  validateSearch: (s: Record<string, unknown>): CustomerDirectorySearch => {
    const str = (v: unknown) => (typeof v === "string" && v !== "" ? v : undefined);
    // Company ids can look numeric; the router JSON-parses search values.
    // Logins, too ("12345" comes back as a number).
    const id = (v: unknown) => (typeof v === "number" ? String(v) : str(v));
    return {
      q: id(s.q),
      tab: s.tab === "companies" || s.tab === "favorites" ? s.tab : undefined,
      sel: id(s.sel),
      company: id(s.company),
      invalid: s.invalid === true || s.invalid === "true" ? true : undefined,
    };
  },
  component: CustomerDirectoryPage,
});

const agentCustomerRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/customers/$login",
  component: CustomerDetailPage,
});

const agentPluginRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/plugins/$pluginId/$",
  component: PluginRoutePage,
});

const agentDialRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/dial",
  validateSearch: validateDialSearch,
  component: DialPage,
});

// /portal/login: mounts its own CustomerAuthProvider (a separate session from
// the agent AuthProvider) — not gated, since it must render for a
// not-yet-authenticated customer.
const portalLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portal/login",
  validateSearch: (
    s: Record<string, unknown>,
  ): {
    next?: string;
    sso_error?: string;
    logged_out?: string;
    sso_step_up?: "1";
  } => ({
    next: typeof s.next === "string" ? s.next : undefined,
    // The backend's "1" sends the browser back to finish a second factor after SSO.
    sso_step_up: searchFlag(s.sso_step_up) === "1" ? "1" : undefined,
    // The router JSON-parses search values: `?sso_error=1` arrives as a number.
    sso_error: searchFlag(s.sso_error),
    // Only the backend's "1" marks a sign-out; "0"/"false" must not show the note.
    logged_out: searchFlag(s.logged_out) === "1" ? "1" : undefined,
  }),
  component: () => (
    <RequirePortalEnabled>
      <CustomerAuthProvider>
        <PortalLoginPage />
      </CustomerAuthProvider>
    </RequirePortalEnabled>
  ),
});

// /portal: gated portal shell — CustomerAuthProvider + RequirePortalAuth.
const portalLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/portal",
  component: () => (
    <RequirePortalEnabled>
      <CustomerAuthProvider>
        <RequirePortalAuth>
          <PortalShell>
            <Outlet />
          </PortalShell>
        </RequirePortalAuth>
      </CustomerAuthProvider>
    </RequirePortalEnabled>
  ),
});

const portalIndexRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/",
  validateSearch: (s: Record<string, unknown>): PortalTicketListSearch => {
    const state =
      s.state_type === "open" ||
      s.state_type === "pending" ||
      s.state_type === "closed" ||
      s.state_type === "all"
        ? s.state_type
        : undefined;
    return { state_type: state };
  },
  component: TicketListPage,
});

const portalNewTicketRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/tickets/new",
  validateSearch: (s: Record<string, unknown>): PortalNewTicketSearch => {
    const n = Number(s.ci);
    return { ci: Number.isInteger(n) && n > 0 ? n : undefined };
  },
  component: NewTicketPage,
});

// Literal path; /portal/tickets/$ticketId cannot capture it.
const portalNewProcessTicketRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/process/new",
  validateSearch: (s: Record<string, unknown>): PortalNewProcessTicketSearch => ({
    process: typeof s.process === "string" && s.process !== "" ? s.process : undefined,
  }),
  component: PortalNewProcessTicketPage,
});

const portalTicketRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/tickets/$ticketId",
  component: TicketDetailPage,
});

const portalKbRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/kb",
  validateSearch: (s: Record<string, unknown>): PortalKbSearch => ({
    q: typeof s.q === "string" ? s.q : undefined,
  }),
  component: KbSearchPage,
});

const portalKbArticleRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/kb/$slug",
  component: KbArticlePage,
});

const portalChangeScheduleRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/change-schedule",
  component: PortalChangeSchedulePage,
});

const portalPreferencesRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/preferences",
  component: PortalPreferencesPage,
});

const portalSecurityRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/security",
  component: PortalSecurityPage,
});

// /admin: agent session (RequireAuth) + is_admin from /me (RequireAdmin)
// gated shell with a grouped left sidebar nav (see AdminShell).
const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: () => (
    <RequireAdmin>
      <AdminShell>
        <Outlet />
      </AdminShell>
    </RequireAdmin>
  ),
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/",
  component: AdminHomePage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/users",
  component: UsersPage,
});

const adminAuthConfigRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/auth-config",
  component: AuthConfigPage,
});

const adminIdentityProvidersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/identity-providers",
  component: IdentityProvidersPage,
});

const adminIdentityProviderRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/identity-providers/$providerId",
  validateSearch: (s: Record<string, unknown>): IdentityProviderSearch => ({
    tab:
      s.tab === "connection" ||
      s.tab === "mapping" ||
      s.tab === "roles" ||
      s.tab === "provisioning" ||
      s.tab === "session" ||
      s.tab === "test" ||
      s.tab === "links"
        ? s.tab
        : undefined,
    test: typeof s.test === "string" && s.test ? s.test : undefined,
  }),
  component: IdentityProviderPage,
});

const adminGroupsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/groups",
  component: GroupsPage,
});

const adminRolesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/roles",
  component: RolesPage,
});

const adminAgentRolesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/agent-roles",
  component: AgentRolesPage,
});

const adminAgentGroupsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/agent-groups",
  component: AgentGroupsPage,
});

const adminRoleGroupsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/role-groups",
  component: RoleGroupsPage,
});

const adminQueuesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/queues",
  component: AdminQueuesPage,
});

const adminStatesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/states",
  component: StatesPage,
});

const adminPrioritiesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/priorities",
  component: PrioritiesPage,
});

const adminTypesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/types",
  component: TypesPage,
});

const adminServicesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/services",
  component: ServicesPage,
});

const adminSlasRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/slas",
  component: SlasPage,
});

const adminSystemAddressesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/system-addresses",
  component: SystemAddressesPage,
});

const adminNotificationEventsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/notification-events",
  component: NotificationEventsPage,
});

const adminCustomerUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/customer-users",
  component: CustomerUsersPage,
});

const adminCustomerCompaniesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/customer-companies",
  component: CustomerCompaniesPage,
});

const adminCustomerUserCustomersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/customer-user-customers",
  component: CustomerUserCustomersPage,
});

const adminCustomerUserGroupsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/customer-user-groups",
  component: CustomerUserGroupsPage,
});

const adminTemplatesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/templates",
  component: TemplatesPage,
});

const adminTemplateAttachmentsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/template-attachments",
  component: TemplateAttachmentsPage,
});

const adminAttachmentsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/attachments",
  component: AttachmentsPage,
});

const adminQueueTemplatesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/queue-templates",
  component: QueueTemplatesPage,
});

const adminQueueAutoResponsesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/queue-auto-responses",
  component: QueueAutoResponsesPage,
});

const adminSalutationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/salutations",
  component: SalutationsPage,
});

const adminSignaturesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/signatures",
  component: SignaturesPage,
});

const adminAutoResponsesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/auto-responses",
  component: AutoResponsesPage,
});

const adminDynamicFieldsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/dynamic-fields",
  component: DynamicFieldsPage,
});

const adminWebhooksRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/webhooks",
  component: WebhooksPage,
});

const adminWebservicesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/webservices",
  component: WebservicesPage,
});

const adminWebserviceDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/webservices/$wsId",
  validateSearch: (s: Record<string, unknown>): { tab: WebserviceTab } => ({
    tab: WEBSERVICE_TABS.includes(s.tab as WebserviceTab) ? (s.tab as WebserviceTab) : "overview",
  }),
  component: WebserviceDetailPage,
});

const adminItsmRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/itsm",
  component: ItsmPage,
});

const adminChangeRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/itsm/change",
  // The router JSON-parses search values: the page falls back to the first tab for anything unknown.
  validateSearch: (s: Record<string, unknown>): { tab?: ChangeAdminTab } => {
    const tab = parseChangeAdminTab(s.tab);
    return { tab: tab === "templates" ? undefined : tab };
  },
  component: ChangeAdminPage,
});

const adminChangeTemplateRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/itsm/change/templates/$templateId",
  component: ChangeTemplateEditorPage,
});

const adminItsmLinkTypesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/itsm/link-types",
  component: LinkTypesPage,
});

const adminItsmImportRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/itsm-import",
  component: ItsmImportPage,
});

const adminApiKeysRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/api-keys",
  component: ApiKeysPage,
});

const adminMailOutboundRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/mail-outbound",
  component: MailOutboundPage,
});

const adminPgpRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/pgp",
  component: PgpKeysPage,
});

const adminSmimeRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/smime",
  component: SmimePage,
});

const adminMailAccountsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/mail-accounts",
  component: MailAccountsPage,
});

const adminTelegramRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/telegram",
  component: TelegramChannelPage,
});

const adminOAuth2TokensRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/oauth2-tokens",
  component: OAuth2TokensPage,
});

const adminMailLogRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/mail-log",
  component: MailLogPage,
});

const adminSubjectConfigRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/subject-config",
  component: SubjectConfigPage,
});

const adminPostmasterFiltersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/postmaster-filters",
  component: PostmasterFiltersPage,
});

const adminPostmasterFilterDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/postmaster-filters/$name",
  component: PostmasterFilterDetailPage,
});

const adminAclRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/acl",
  component: AclPage,
});

const adminAclDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/acl/$aclId",
  component: AclDetailPage,
});

const adminTicketAttributeRelationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ticket-attribute-relations",
  component: TicketAttributeRelationsPage,
});

const adminGenericAgentJobsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/generic-agent-jobs",
  component: GenericAgentJobsPage,
});

const adminGenericAgentJobDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/generic-agent-jobs/$jobName",
  component: GenericAgentJobDetailPage,
});

const adminProcessesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/processes",
  component: ProcessesPage,
});

const adminQueueVariablesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/queue-variables",
  component: QueueVariablesPage,
});

const adminQueueCustomerLinksRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/queue-customer-links",
  component: QueueCustomerLinksPage,
});

const adminCustomerFieldsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/customer-fields",
  component: CustomerFieldsPage,
});

const adminGdprRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/gdpr",
  validateSearch: (s: Record<string, unknown>): GdprSearch => ({
    logins: typeof s.logins === "string" ? s.logins : undefined,
    tab: s.tab === "jobs" || s.tab === "run" ? s.tab : undefined,
  }),
  component: GdprPage,
});

const adminDaemonsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/daemons",
  component: DaemonsPage,
});

const adminPluginsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/plugins",
  // The router JSON-parses search values: check for a plain string, never assume one.
  validateSearch: (s: Record<string, unknown>): { tab?: "available" | "repositories" } => ({
    tab: s.tab === "available" || s.tab === "repositories" ? s.tab : undefined,
  }),
  component: PluginsPage,
});

const adminReportingRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/reporting",
  component: ReportingAdminPage,
});

const adminDashboardTemplatesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/dashboard-templates",
  component: DashboardTemplatesPage,
});

const adminDashboardKioskLinksRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/dashboard-kiosk-links",
  component: DashboardKioskLinksPage,
});

const adminSettingsSectionRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/settings/$section",
  component: SettingsSectionPage,
});

const adminSystemRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/system",
  component: SystemInfoPage,
});

const adminProcessDetailRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/processes/$processEntityId",
  // Lazy: keeps @xyflow/react out of the main chunk.
  component: lazyRouteComponent(
    () => import("@/routes/admin/process-designer/ProcessDesignerPage"),
    "ProcessDesignerPage",
  ),
});

const adminAiRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai",
  component: AiSettingsPage,
});

const adminAiProvidersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/providers",
  component: AiProvidersPage,
});

const adminAiModelsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/models",
  component: AiModelsPage,
});

const adminAiMcpRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/mcp",
  component: AiMcpClientsPage,
});

const adminWebchatRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/webchat",
  component: WebchatConfigsPage,
});

const adminWebchatEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/webchat/$configId",
  component: WebchatConfigEditorPage,
});

const adminAiQueuesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/queues",
  component: AiQueuePoliciesPage,
});

const adminAiQueueNewRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/queues/new",
  component: AiQueuePolicyNewPage,
});

const adminAiQueueEditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/queues/$policyId",
  component: AiQueuePolicyEditPage,
});

const adminAiAuditRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/audit",
  component: AiAuditPage,
});

const adminAiAclRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/ai/acl",
  component: AiAclPage,
});

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "$",
  beforeLoad: () => {
    throw redirect({ to: "/" });
  },
});

// CMDB (wave 4).
const agentCmdbRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/cmdb",
  validateSearch: validateCmdbListSearch,
  component: CmdbListPage,
});

const agentCmdbNewRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/cmdb/new",
  validateSearch: (s: Record<string, unknown>): { class?: number } => {
    const n = Number(s.class);
    return Number.isInteger(n) && n > 0 ? { class: n } : {};
  },
  component: CiNewRoute,
});

const agentCmdbImportRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/cmdb/import",
  validateSearch: (s: Record<string, unknown>): { class?: number } => {
    const n = Number(s.class);
    return Number.isInteger(n) && n > 0 ? { class: n } : {};
  },
  component: CmdbImportPage,
});

const agentCmdbCiRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/cmdb/$ciId",
  validateSearch: validateCiDetailSearch,
  component: CmdbCiDetailPage,
});

const agentCmdbCiEditRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/cmdb/$ciId/edit",
  component: CiEditRoute,
});

const adminCmdbClassesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/cmdb/classes",
  component: CmdbClassesPage,
});

const adminCmdbClassRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/cmdb/classes/$classId",
  component: CmdbClassEditorPage,
});

const adminCmdbCatalogRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/cmdb/catalog",
  component: CmdbCatalogPage,
});

const portalCmdbListRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/config-items",
  component: PortalCisPage,
});

const portalCmdbDetailRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/config-items/$ciId",
  component: PortalCiPage,
});

const agentApprovalsRoute = createRoute({
  getParentRoute: () => agentLayoutRoute,
  path: "/approvals",
  validateSearch: parseApprovalsSearch,
  component: ApprovalsPage,
});

const portalApprovalsRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/approvals",
  component: PortalApprovalsPage,
});

const portalApprovalRoute = createRoute({
  getParentRoute: () => portalLayoutRoute,
  path: "/approvals/$approvalId",
  component: PortalApprovalPage,
});

const adminApprovalsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/approvals",
  component: ApprovalsOverviewPage,
});

const adminApprovalTemplatesRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/approvals/templates",
  component: ApprovalTemplatesPage,
});

const adminApprovalListsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/approvals/lists",
  component: ApprovalListsPage,
});

const adminApprovalDelegationsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: "/approvals/delegations",
  component: ApprovalDelegationsPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  setPasswordRoute,
  approvalLinkRoute,
  kioskWallboardRoute,
  agentLayoutRoute.addChildren([
    agentIndexRoute,
    agentWallboardRoute,
    agentQueuesRoute,
    agentNewTicketRoute,
    agentNewProcessTicketRoute,
    agentDrillRoute,
    agentTicketRoute,
    agentSearchRoute,
    agentKbRoute,
    agentKbNewRoute,
    agentKbCategoriesRoute,
    agentKbArticleRoute,
    agentKbArticleEditRoute,
    agentSecurityRoute,
    agentSettingsRoute,
    agentStatsRoute,
    agentReportsRoute,
    agentReportNewRoute,
    agentReportEditRoute,
    agentReportSchedulesRoute,
    agentReportScheduleNewRoute,
    agentReportScheduleRoute,
    agentReportViewRoute,
    agentSearchNewRoute,
    agentSearchEditRoute,
    agentSearchViewRoute,
    agentItsmCisRoute,
    agentItsmCiDetailRoute,
    agentChangesRoute,
    agentChangeCalendarRoute,
    agentChangeRoute,
    agentCabMeetingsRoute,
    agentCabMeetingRoute,
    agentLegacyChangesRoute,
    agentLegacyChangeRoute,
    agentCalendarRoute,
    agentTemplatesRoute,
    agentServicesRoute,
    agentTimeAccountingRoute,
    agentCustomersRoute,
    agentCustomerRoute,
    agentDialRoute,
    agentPluginRoute,
    agentCmdbRoute,
    agentCmdbNewRoute,
    agentCmdbImportRoute,
    agentCmdbCiRoute,
    agentCmdbCiEditRoute,
    agentApprovalsRoute,
  ]),
  portalLoginRoute,
  portalLayoutRoute.addChildren([
    portalIndexRoute,
    portalNewTicketRoute,
    portalNewProcessTicketRoute,
    portalTicketRoute,
    portalKbRoute,
    portalKbArticleRoute,
    portalPreferencesRoute,
    portalSecurityRoute,
    portalCmdbListRoute,
    portalCmdbDetailRoute,
    portalApprovalsRoute,
    portalChangeScheduleRoute,
    portalApprovalRoute,
  ]),
  adminLayoutRoute.addChildren([
    adminIndexRoute,
    adminUsersRoute,
    adminAuthConfigRoute,
    adminIdentityProvidersRoute,
    adminIdentityProviderRoute,
    adminGroupsRoute,
    adminRolesRoute,
    adminAgentRolesRoute,
    adminAgentGroupsRoute,
    adminRoleGroupsRoute,
    adminQueuesRoute,
    adminStatesRoute,
    adminPrioritiesRoute,
    adminTypesRoute,
    adminServicesRoute,
    adminSlasRoute,
    adminSystemAddressesRoute,
    adminNotificationEventsRoute,
    adminCustomerUsersRoute,
    adminCustomerCompaniesRoute,
    adminCustomerUserCustomersRoute,
    adminCustomerUserGroupsRoute,
    adminTemplatesRoute,
    adminTemplateAttachmentsRoute,
    adminAttachmentsRoute,
    adminQueueTemplatesRoute,
    adminQueueAutoResponsesRoute,
    adminSalutationsRoute,
    adminSignaturesRoute,
    adminAutoResponsesRoute,
    adminDynamicFieldsRoute,
    adminWebhooksRoute,
    adminWebservicesRoute,
    adminItsmRoute,
    adminChangeRoute,
    adminChangeTemplateRoute,
    adminItsmLinkTypesRoute,
    adminItsmImportRoute,
    adminApprovalsRoute,
    adminApprovalTemplatesRoute,
    adminApprovalListsRoute,
    adminApprovalDelegationsRoute,
    adminWebserviceDetailRoute,
    adminApiKeysRoute,
    adminMailOutboundRoute,
    adminPgpRoute,
    adminSmimeRoute,
    adminMailAccountsRoute,
    adminTelegramRoute,
    adminOAuth2TokensRoute,
    adminMailLogRoute,
    adminSubjectConfigRoute,
    adminPostmasterFiltersRoute,
    adminPostmasterFilterDetailRoute,
    adminAclRoute,
    adminTicketAttributeRelationsRoute,
    adminAclDetailRoute,
    adminGenericAgentJobsRoute,
    adminGenericAgentJobDetailRoute,
    adminProcessesRoute,
    adminProcessDetailRoute,
    adminQueueVariablesRoute,
    adminQueueCustomerLinksRoute,
    adminCustomerFieldsRoute,
    adminGdprRoute,
    adminDaemonsRoute,
    adminPluginsRoute,
    adminReportingRoute,
    adminDashboardTemplatesRoute,
    adminDashboardKioskLinksRoute,
    adminSettingsSectionRoute,
    adminSystemRoute,
    adminAiRoute,
    adminAiProvidersRoute,
    adminAiModelsRoute,
    adminAiMcpRoute,
    adminWebchatRoute,
    adminWebchatEditRoute,
    adminAiQueuesRoute,
    adminAiQueueNewRoute,
    adminAiQueueEditRoute,
    adminAiAuditRoute,
    adminAiAclRoute,
    adminCmdbClassesRoute,
    adminCmdbClassRoute,
    adminCmdbCatalogRoute,
  ]),
  catchAllRoute,
]);

// Vite serves the demo under a project sub-path (e.g. "/tiqora/demo/").
// TanStack Router needs the same prefix or every <Navigate to="/agent" /> and
// Link would jump to the host root (https://…github.io/agent) and 404.
const routerBasepath = (import.meta.env.BASE_URL || "/").replace(/\/+$/, "") || "/";

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  basepath: routerBasepath,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
